import type { DocumentNode } from 'apollo-link';
import type { Maybe } from '@/helpers/types';

import { getAppVersion } from '@/graphql/utils/links';
import { getAxiosCached } from './axios-helper';
import { generateToken } from '@/helpers/user-helper';

/** Pass an access token if the response behavior depends on auth state. */
export async function headerfulCachedRequest(
	query: string | DocumentNode,
	variables: any,
	jwId: Maybe<string>,
	accessToken?: Maybe<string>
) {
	let freshToken: Maybe<string> = accessToken;
	try {
		if (accessToken != null) freshToken = await generateToken(accessToken, jwId);
	} catch {
		freshToken = undefined;
	}

	const headers: Record<string, string> = {
		'content-type': 'application/json',
		'App-Version': getAppVersion(),
	};

	if (freshToken) headers.Authorization = `Bearer ${freshToken}`;

	// only add client headers if client is doing the request (SPA).
	// otherwise we could run in caching problems, as the headers are also taken into account for cache keys.
	if (process.client) {
		const { X_RATE_LIMIT_SHORTCUT } = process.env;
		if (X_RATE_LIMIT_SHORTCUT) {
			headers['X-Rate-Limit-Shortcut'] = X_RATE_LIMIT_SHORTCUT;
		}

		if (jwId) headers['Device-Id'] = jwId;
	}

	try {
		const res = await getAxiosCached().request({
			method: 'POST',
			url: JW_CONFIG.GRAPHQL_URL,
			headers,
			data: { query: getQuery(query), variables },
		});

		return res.data;
	} catch (error: any) {
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry('core cached request failed', { error, where: 'request-helper.ts' }, 'error');

		// Re-throw so callers can handle the error appropriately
		throw error;
	}
}

export function getQuery(query: string | DocumentNode): string {
	if ((query as DocumentNode).loc) {
		return (query as DocumentNode).loc?.source.body.replace(/[\s,]+/g, ' ').trim() || '';
	}

	return query as string;
}
